import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatSort, MatSortHeader, Sort, SortDirection } from '@angular/material/sort';

import { DIALOG_DATA, DialogComponent, DialogRef, SpinnerDirective } from '@rp/shared/components';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { DateFormat, SortingRequest, StructureResponse } from '@rp/shared/models';
import { CentsToDollarsPipe } from '@rp/shared/pipes';
import { CommonMediaQueries, MediaScreenService } from '@rp/shared/services';

import { FinanceStatusComponent } from '../finance-status/finance-status.component';
import { DETAILS_DIALOG_COLUMNS } from '../../consts/details-dialog-columns';
import { Payout, PayoutHistory, PayoutHistoryRequest } from '../../models';
import { FINANCE_WEBMASTER_PROVIDER_TOKEN, HttpFinanceWebmasterProvider } from '../../providers';

@Component({
  selector: 'rp-details-dialog',
  standalone: true,
  templateUrl: './details-dialog.component.html',
  styleUrl: './details-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogComponent,
    TranslateModule,
    FinanceStatusComponent,
    SpinnerDirective,
    DatePipe,
    CentsToDollarsPipe,
    AsyncPipe,
    MatSortHeader,
    MatSort,
  ],
  providers: [
    { provide: FINANCE_WEBMASTER_PROVIDER_TOKEN, useClass: HttpFinanceWebmasterProvider },
    DatePipe,
  ],
})
export class DetailsDialogComponent implements OnInit {
  parent = parent;
  DateFormat = DateFormat;

  isMobile$: Observable<boolean>;

  data = input<Payout>({} as Payout);
  columns = signal(DETAILS_DIALOG_COLUMNS);
  items = signal<PayoutHistory[]>([]);
  isLoading = signal<boolean>(true);
  sortDirection = signal<SortDirection>('desc');

  historyParams = computed<PayoutHistoryRequest>(() => ({
    payoutId: this.dialogData.payout.id,
    ...this._sorting(),
  }));

  dialogRef = inject(DialogRef);
  dialogData: {
    payout: Payout;
    statuses: WritableSignal<Map<number, string>>;
    structures: WritableSignal<StructureResponse>;
    shouldUpdatePayoutsSubject: Subject<boolean>;
  } = inject(DIALOG_DATA);

  private _sorting = signal<SortingRequest>({
    sorting: '-date',
  });

  private _destroyRef = inject(DestroyRef);
  private _financeWebmasterProvider = inject(FINANCE_WEBMASTER_PROVIDER_TOKEN);
  private _mediaScreenService = inject(MediaScreenService);

  ngOnInit(): void {
    this.isMobile$ = this._mediaScreenService.mediaMatcher(CommonMediaQueries.MD, true);
    this.isLoading.set(true);

    this._getHistory();
  }

  onSortChange(sort: Sort) {
    const { direction } = sort;
    const sortMap: Record<string, string> = {
      asc: 'date',
      desc: '-date',
    };
    const sorting = sortMap[direction] ? { sorting: sortMap[direction] } : {};

    this._sorting.set(sorting);
    this.sortDirection.set(sort.direction);

    this._getHistory();
  }

  private _getHistory(): void {
    this._financeWebmasterProvider
      .getPayoutsHistory(this.historyParams())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(payouts => {
        this.items.set(payouts);
        this.isLoading.set(false);
      });
  }
}
